import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable from 'react-data-table-component';
import axios from "axios";
import {
    Spinner,
    Badge,
    Button,
    Card,
    Row,
    Col,
    Dropdown
} from "react-bootstrap";
import { toast, ToastContainer } from 'react-toastify';
import {
    FiSearch,
    FiFileText,
    FiFile,
    FiRefreshCw,
    FiDownload,
    FiUsers,
    FiUserCheck,
    FiUserX,
    FiAlertTriangle,
    FiX,
    FiUser,
    FiLink,
    FiClock,
    FiXCircle,
    FiCheckCircle,
    FiLogIn,
    FiChevronDown
} from "react-icons/fi";
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from "jspdf-autotable";
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import StatCard from '../Shared/StatCard';
import AddGuarantorModal from '../CustomerDashboard/AddGuarantor';

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [fees, setFees] = useState([]);
    const [products, setProducts] = useState([]);
    const [displayData, setDisplayData] = useState([]);
    const [incompleteData, setIncompleteData] = useState({
        missing: [],
        failed: [],
        allIncomplete: []
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState("");
    const [activeFilter, setActiveFilter] = useState(null);
    const navigate = useNavigate();
    const hasFetched = useRef(false);
    const [showGuarantorModal, setShowGuarantorModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showAnswer, setShowAnswer] = useState(false);
    const [randomMessage, setRandomMessage] = useState("");
    const [customerStatsCollapsed, setCustomerStatsCollapsed] = useState(true);
    const [egiroStatsCollapsed, setEgiroStatsCollapsed] = useState(true);

    const reassuranceMessages = [
        "No worries, you can add one now 🙂",
        "No stress — add it anytime 😌",
        "All good! You can add one now 👍",
        "That’s okay, you can continue 😊",
        "No problem at all — add later 👌",
        "Take your time, you’re doing great 😊",
        "Whenever you’re ready, we’re here 👍",
        "No rush — you’re on the right track 😌",
        "It’s absolutely fine, let’s continue 👌",
        "You’re almost there — take it easy 🙂",
        "Nothing urgent, add it when comfortable 🤝",
        "All set so far, we can do this later 😄",
        "No pressure at all — proceed calmly 🌿",
        "You’re doing perfectly, no need to hurry ✨",
        "We can handle this step together 👍",
        "Everything looks good so far 😊",
        "Relax — this can be updated anytime 😌",
        "No concerns, we’ve got you covered 🤗"
    ];

    const handleAddGuarantorClick = () => {
        if (!displayData.length) {
            toast.warn("No customers to add guarantor for");
            return;
        }
        setShowGuarantorModal(true);
    };

    const refreshAllData = async () => {
        await fetchCustomersData();
        if (userRole !== "viewer") {
            await fetchIncompleteCustomers();
        }
        // Update display data
        setDisplayData(getBaseData());
    };

    const stats = useMemo(() => {
        const canAccess = JSON.parse(localStorage.getItem("can_access") || "0");
        const userName = localStorage.getItem("user_name") || "";

        let filteredCustomers = [...customers];

        if (canAccess !== 1) {
            filteredCustomers = customers.filter(c =>
                c.staff_name === userName
            );
        }

        const filteredRefNos = new Set(filteredCustomers.map(c => c.boDDARefNo).filter(Boolean));

        const missingCount = incompleteData.missing?.filter(refNo =>
            filteredRefNos.has(refNo)
        ).length || 0;

        const failedCount = incompleteData.failed?.filter(refNo =>
            filteredRefNos.has(refNo)
        ).length || 0;

        const totalIncompleteCount = missingCount + failedCount;

        const missingCustomers = incompleteData.missing?.length > 0
            ? filteredCustomers.filter(c => incompleteData.missing.includes(c.boDDARefNo))
            : [];

        const failedCustomers = incompleteData.failed?.length > 0
            ? filteredCustomers.filter(c => incompleteData.failed.includes(c.boDDARefNo))
            : [];

        const actualCustomers = filteredCustomers.filter(c =>
            !c.guarantor_for || c.guarantor_for === ""
        );

        const customersWithGuarantor = filteredCustomers.filter(customer => {
            if (customer.has_guarantor !== 1) return false;
            const hasGuarantor = filteredCustomers.some(guarantor =>
                guarantor.guarantor_for === customer.id_number
            );
            return hasGuarantor;
        });

        const guarantors = filteredCustomers.filter(c =>
            c.guarantor_for && c.guarantor_for !== ""
        );

        const totalCustomers = actualCustomers.length;

        const justSignedUpCustomers = actualCustomers.filter(c =>
            !c.boDDARefNo || c.boDDARefNo === ""
        );
        const justSignedUpCount = justSignedUpCustomers.length;

        const activeCustomersCount = totalCustomers - totalIncompleteCount - justSignedUpCount;

        const activeCustomers = actualCustomers.filter(c =>
            c.boDDARefNo &&
            c.boDDARefNo !== "" &&
            !incompleteData.missing?.includes(c.boDDARefNo) &&
            !incompleteData.failed?.includes(c.boDDARefNo)
        );

        return {
            totalCustomers,
            customersWithGuarantor: customersWithGuarantor.length,
            guarantors: guarantors.length,
            missingCount,
            failedCount,
            totalIncompleteCount,
            justSignedUpCount,
            activeCustomersCount,
            missingCustomers,
            failedCustomers,
            justSignedUpCustomers,
            activeCustomers,
            allCustomers: actualCustomers,
            allGuarantors: guarantors,
            allCustomersWithGuarantor: customersWithGuarantor,
            filteredCustomers
        };
    }, [customers, incompleteData]);

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "-";

        try {
            const date = new Date(dateStr);
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            });
        } catch (error) {
            return dateStr;
        }
    };

    const formatPaymentDay = (dateStr) => {
        if (!dateStr) return "-";

        try {
            const date = new Date(dateStr);
            return date.getDate().toString();
        } catch (error) {
            return "-";
        }
    };

    const getBaseData = () => {
        const canAccess = JSON.parse(localStorage.getItem("can_access") || "0");
        const userName = localStorage.getItem("user_name") || "";


        const getFilteredCustomers = (customerList) => {
            if (canAccess === 1) {
                return customerList;
            }
            return customerList.filter(c => c.staff_name === userName);
        };

        let sourceCustomers = getFilteredCustomers(customers);
        let sourceStats = {
            ...stats,
            missingCustomers: getFilteredCustomers(stats.missingCustomers || []),
            failedCustomers: getFilteredCustomers(stats.failedCustomers || []),
            activeCustomers: getFilteredCustomers(stats.activeCustomers || []),
            allCustomers: getFilteredCustomers(stats.allCustomers || [])
        };

        if (activeFilter) {
            switch (activeFilter.type) {
                case 'justSignedUp':
                    return sourceCustomers.filter(customer =>
                        (!customer.guarantor_for || customer.guarantor_for === "") &&
                        (!customer.boDDARefNo || customer.boDDARefNo === "")
                    ).map(customer => ({
                        ...customer,
                        is_guarantor: false,
                        type: "Just Signed Up",
                        has_guarantor_flag: customer.has_guarantor === 1 ? "Yes" : "No"
                    }));
                case 'withGuarantor':
                    return sourceCustomers.filter(customer =>
                        customer.has_guarantor === 1
                    ).map(customer => {
                        const guarantor = sourceCustomers.find(g =>
                            g.guarantor_for === customer.id_number
                        );
                        return {
                            ...customer,
                            guarantor_name: guarantor?.full_name || "Not Assigned",
                            guarantor_id: guarantor?.customer_id || "",
                            guarantor_id_number: guarantor?.id_number || "",
                            customer_id_number: customer.id_number || "",
                            is_guarantor: false,
                            type: "Customer with Guarantor"
                        };
                    });
                case 'guarantors':
                    return sourceCustomers.filter(customer =>
                        customer.guarantor_for && customer.guarantor_for !== ""
                    ).map(guarantor => {
                        const guaranteedCustomer = sourceCustomers.find(c =>
                            c.id_number === guarantor.guarantor_for
                        );
                        return {
                            ...guarantor,
                            is_guarantor: true,
                            type: "Guarantor",
                            guaranteed_customer_name: guaranteedCustomer?.full_name || "Unknown",
                            guaranteed_customer_id: guaranteedCustomer?.customer_id || "",
                            guaranteed_customer_id_number: guarantor.guarantor_for || "",
                            guarantor_id_number: guarantor.id_number || "",
                            total_guaranteed: 1
                        };
                    });
                case 'missing':
                    return sourceStats.missingCustomers.map(customer => ({
                        ...customer,
                        type: "Missing",
                        is_guarantor: false,
                    }));
                case 'failed':
                    return sourceStats.failedCustomers.map(customer => ({
                        ...customer,
                        type: "Failed",
                        is_guarantor: false,
                    }));
                case 'incomplete':
                    const allIncomplete = [...sourceStats.missingCustomers, ...sourceStats.failedCustomers]
                        .map(customer => ({
                            ...customer,
                            type: customer.boDDARefNo && incompleteData.failed.includes(customer.boDDARefNo)
                                ? "Failed"
                                : "Missing",
                            is_guarantor: false,
                        }));
                    return allIncomplete;
                case 'active':
                    return sourceStats.activeCustomers.map(customer => ({
                        ...customer,
                        is_guarantor: false,
                        type: "Active Customer",
                        has_guarantor_flag: customer.has_guarantor === 1 ? "Yes" : "No"
                    }));
                default:
                    return sourceCustomers.filter(customer =>
                        !customer.guarantor_for || customer.guarantor_for === ""
                    ).map(customer => ({
                        ...customer,
                        is_guarantor: false,
                        type: "Customer",
                        has_guarantor_flag: sourceCustomers.some(g =>
                            g.guarantor_for === customer.id_number
                        ) ? "Yes" : "No"
                    }));
            }
        } else {
            return sourceStats.allCustomers.map(customer => ({
                ...customer,
                is_guarantor: false,
                type: "Customer",
                has_guarantor_flag: sourceCustomers.some(g =>
                    g.guarantor_for === customer.id_number
                ) ? "Yes" : "No"
            }));
        }
    };

    useEffect(() => {
        let data = getBaseData();

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(item => {
                const fieldsToSearch = [
                    item.full_name,
                    item.email_address,
                    item.phone_number,
                    item.customer_id,
                    item.id_number,
                    item.bill_number,
                    item.product_name,
                    item.staff_name,
                    item.boDDARefNo,
                    item.boName,
                    item.boTransactionRefNo,
                    item.clientID,
                    item.purpose,
                    item.segment,
                    item.id_type,
                    item.applicantBankCode,
                    item.status,
                    item.requestID,
                    item.requestType,
                    item.guarantor_name,
                    item.guaranteed_customer_name,
                ];

                let reportsText = '';
                if (item.reports) {
                    try {
                        const reports = typeof item.reports === 'string' ? JSON.parse(item.reports) : item.reports;
                        if (Array.isArray(reports)) {
                            reportsText = reports.map(r => `${r.month} ${r.status} ${r.comments || ''}`).join(' ');
                        }
                    } catch (error) {
                    }
                }

                let logsText = '';
                if (item.logs && Array.isArray(item.logs)) {
                    logsText = item.logs.map(log => `${log.staff_name || ''} ${log.login_time || ''}`).join(' ');
                }

                return fieldsToSearch.some(field => {
                    if (field === null || field === undefined) return false;
                    return String(field).toLowerCase().includes(term);
                }) || reportsText.toLowerCase().includes(term) || logsText.toLowerCase().includes(term);
            });
        }

        setDisplayData(data);
    }, [searchTerm, customers, activeFilter, incompleteData]);

    const handleStatCardClick = (filterType) => {
        if (activeFilter && activeFilter.type === filterType) {
            setActiveFilter(null);
        } else {
            setActiveFilter({ type: filterType });
        }
    };

    const clearFilters = () => {
        setActiveFilter(null);
        setSearchTerm("");
    };

    const fetchIncompleteCustomers = async () => {
        try {
            const response = await axios.get("https://www.azzenterprises.com/api/customers/incomplete-creation", {
                withCredentials: true
            });

            setIncompleteData({
                missing: response.data.notExists || [],
                failed: response.data.failed || [],
                allIncomplete: response.data.allIncomplete || []
            });
        } catch (err) {
            console.error("Error fetching incomplete customers:", err);
        }
    };

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const userRes = await axios.get("https://www.azzenterprises.com/api/admin/getuser", {
                withCredentials: true
            });

            const user = userRes.data;
            setUserRole(user.user_role || "viewer");
            localStorage.setItem("can_access", JSON.stringify(user.can_access || 0));
            localStorage.setItem("user_name", user.user_name || "");

            await fetchCustomersData();

            if (user.user_role !== "viewer") {
                await fetchIncompleteCustomers();
            }
        } catch (err) {
            const serverMsg = err.response?.data?.error || err.message;
            setError(serverMsg);

            if (serverMsg === "Token required") {
                if (!toast.isActive("token-expired")) {
                    toast.error("Session expired. Redirecting to login...", { toastId: "token-expired" });
                }
                setTimeout(() => navigate("/login"), 1300);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomersData = async () => {
        try {
            setLoading(true);
            const userName = localStorage.getItem("user_name") || "";
            const canAccess = JSON.parse(localStorage.getItem("can_access")) || 0;

            const productsRes = await axios.get("https://www.azzenterprises.com/api/products", {
                withCredentials: true
            });
            setProducts(productsRes.data);

            let customersRes;
            if (canAccess === 1) {
                customersRes = await axios.get("https://www.azzenterprises.com/api/customers", {
                    withCredentials: true
                });
            } else {
                customersRes = await axios.get(`https://www.azzenterprises.com/api/customers/bystaff/${userName}`, {
                    withCredentials: true
                });
            }

            const res = await axios.get("https://www.azzenterprises.com/api/fee/collectionfees", { withCredentials: true });
            setFees(Array.isArray(res.data) ? res.data.filter(fee => fee.status === 1) : []);

            setCustomers(customersRes.data);
            setError(null);
        } catch (err) {
            const serverMsg = err.response?.data?.error || err.message;
            setError(serverMsg);
            toast.error(`Failed to fetch customers: ${serverMsg}`);
            toast.error("Failed to fetch fees");
            setFees([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshAll = async () => {
        await fetchCustomersData();
        if (userRole !== "viewer") {
            await fetchIncompleteCustomers();
        }
    };

    useEffect(() => {
        if (!hasFetched.current) {
            fetchAllData();
            hasFetched.current = true;
        }
        let timeout1, timeout2;

        const startLoop = () => {
            setShowAnswer(false);
            timeout1 = setTimeout(() => {
                const msg =
                    reassuranceMessages[
                    Math.floor(Math.random() * reassuranceMessages.length)
                    ];
                setRandomMessage(msg);
                setShowAnswer(true);
            }, 1500);
            timeout2 = setTimeout(() => {
                startLoop();
            }, 6500);
        };

        startLoop();

        return () => {
            clearTimeout(timeout1);
            clearTimeout(timeout2);
        };
    }, []);

    const formatDate = (dateStr) =>
        dateStr
            ? new Date(dateStr).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            })
            : "-";

    const formatCurrency = (amount) =>
        amount ? `$${parseFloat(amount).toFixed(2)}` : "-";

    const prepareExportData = () =>
        displayData.map((c) => {
            let reportsData = [];
            try {
                reportsData = typeof c.reports === 'string' ? JSON.parse(c.reports) : (c.reports || []);
            } catch (error) {
                reportsData = [];
            }

            const downPayAmount = c.down_pay ?
                parseFloat(c.down_pay) :
                Math.floor(parseFloat(c.bill_amount) / parseFloat(c.emi_plan_duration));

            let collFeeAmount = 0;
            if (c.coll_fee) {
                collFeeAmount = parseFloat(c.coll_fee);
            } else {
                const monthlyAmount = parseFloat(c.bill_amount) / parseFloat(c.emi_plan_duration);
                const fee = fees.find(f => monthlyAmount >= parseFloat(f.range_min) && monthlyAmount <= parseFloat(f.range_max));
                collFeeAmount = fee ? parseFloat(fee.collection_fee) : monthlyAmount;
            }

            const product = products.find(p => p.product_id === Number(c.product_name));
            const productName = product ? product.product_name : c.product_name || "-";

            const baseData = {
                "ID": c.customer_id || "",
                "Full Name": c.full_name || "",
                "Staff": c.staff_name || "",
                "Email": c.email_address || "",
                "Phone": c.phone_number || "",
                "DOB": formatDate(c.date_of_birth),
                "ID Type": c.id_type || "",
                "ID Number": c.id_number || "",
                "Passport Number": c.passport_number || "",
                "Bill No": c.bill_number || "",
                "Bill Amt": formatCurrency(c.bill_amount),
                "Down Pay": formatCurrency(downPayAmount),
                "Coll Fee": formatCurrency(collFeeAmount),
                "Product": productName,
                "Plan": c.emi_plan_duration || "",
                "Payment Day": formatPaymentDay(c.payment_date),
                "DDA Ref": c.boDDARefNo || "",
                "BO Name": c.boName || "",
                "Request Type": c.requestType || "",
                "Flag Status": c.cancelFlag === 1 ? "Yes" : "No",
                "Purchase on": formatDateTime(c.timestamp),
                "Date": formatDateTime(c.date),
                "Has Guarantor": c.has_guarantor === 1 ? "Yes" : "No",
                "Guarantor For": c.guarantor_for || "",
            };

            Object.keys(baseData).forEach(key => {
                if (baseData[key] === null || baseData[key] === undefined || baseData[key] === "") {
                    delete baseData[key];
                }
            });

            if (activeFilter?.type === 'withGuarantor') {
                const extraData = {
                    "Guarantor Name": c.guarantor_name || "",
                    "Guarantor ID": c.guarantor_id || "",
                    "Guarantor ID Number": c.guarantor_id_number || ""
                };

                Object.keys(extraData).forEach(key => {
                    if (extraData[key]) {
                        baseData[key] = extraData[key];
                    }
                });
            } else if (activeFilter?.type === 'guarantors') {
                const extraData = {
                    "Guaranteeing Customer": c.guaranteed_customer_name || "",
                    "Guaranteed Customer ID Number": c.guaranteed_customer_id_number || "",
                    "Total Guaranteed": c.total_guaranteed || 0
                };

                Object.keys(extraData).forEach(key => {
                    if (extraData[key]) {
                        baseData[key] = extraData[key];
                    }
                });
            } else if (activeFilter?.type === 'active') {
                baseData["Status"] = "Active";
            }

            return baseData;
        });

    const handleExportCSV = () => {
        const data = prepareExportData();
        if (data.length === 0) {
            toast.warn("No data to export");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
        XLSX.writeFile(workbook, `customers_${activeFilter?.type || 'all'}_data.csv`, { bookType: "csv" });
        toast.success("CSV exported successfully");
    };

    const handleExportExcel = () => {
        const data = prepareExportData();
        if (data.length === 0) {
            toast.warn("No data to export");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
        XLSX.writeFile(workbook, `customers_${activeFilter?.type || 'all'}_data.xlsx`);
        toast.success("Excel exported successfully");
    };

    const handleExportPDF = () => {
        const data = prepareExportData();
        if (data.length === 0) {
            toast.warn("No data to export");
            return;
        }

        try {
            const headers = [Object.keys(data[0])];
            const body = data.map(item => Object.values(item));

            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: 'a4'
            });

            const margin = 2;
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;

            doc.setFontSize(12);
            doc.setTextColor(30, 41, 59);
            doc.text(`${activeFilter?.type === 'withGuarantor' ? 'Customers with Guarantor' :
                activeFilter?.type === 'guarantors' ? 'Guarantors' :
                    activeFilter?.type === 'missing' ? 'Missing Status' :
                        activeFilter?.type === 'failed' ? 'Failed (No Bank Token)' :
                            activeFilter?.type === 'incomplete' ? 'All Incomplete' :
                                activeFilter?.type === 'justSignedUp' ? 'Just Signed Up' :
                                    activeFilter?.type === 'active' ? 'Active Customers' : 'All Customers'} Report`,
                margin, margin + 8);

            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.text(`Total: ${displayData.length} records | Generated: ${new Date().toLocaleDateString()}`, margin, margin + 14);

            const columnStyles = {
                0: { cellWidth: 5 },
                1: { cellWidth: 18 },
                2: { cellWidth: 12 },
                3: { cellWidth: 25 },
                4: { cellWidth: 10 },
                5: { cellWidth: 12 },
                6: { cellWidth: 7 },
                7: { cellWidth: 10 },
                8: { cellWidth: 12 },
                9: { cellWidth: 12 },
                10: { cellWidth: 12 },
                11: { cellWidth: 15 },
                12: { cellWidth: 10 },
                13: { cellWidth: 10 },
                14: { cellWidth: 10 },
                15: { cellWidth: 15 },
                16: { cellWidth: 15 },
                17: { cellWidth: 20 },
                18: { cellWidth: 12 },
                19: { cellWidth: 10 },
                20: { cellWidth: 18 },
                21: { cellWidth: 18 },
                22: { cellWidth: 15 },
                23: { cellWidth: 12 },
                24: { cellWidth: 12 },
                25: { cellWidth: 15 },
                26: { cellWidth: 30 },
                27: { cellWidth: 8 },
                28: { cellWidth: 8 },
                29: { cellWidth: 8 },
                30: { cellWidth: 8 },
                31: { cellWidth: 25 },
                32: { cellWidth: 18 },
                33: { cellWidth: 12 },
                34: { cellWidth: 15 },
                35: { cellWidth: 18 },
                36: { cellWidth: 12 },
                37: { cellWidth: 15 },
                38: { cellWidth: 12 },
                39: { cellWidth: 20 },
                40: { cellWidth: 12 }
            };

            const actualColumnStyles = {};
            headers[0].forEach((header, index) => {
                actualColumnStyles[index] = columnStyles[index] || { cellWidth: 15 };
            });

            autoTable(doc, {
                head: headers,
                body: body,
                startY: margin + 18,
                styles: {
                    fontSize: 6,
                    cellPadding: 0.3,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.05,
                    textColor: [30, 30, 30],
                    minCellHeight: 3
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 6,
                    halign: 'center',
                    cellPadding: 0.5
                },
                bodyStyles: {
                    fontSize: 5,
                    cellPadding: 0.3
                },
                columnStyles: actualColumnStyles,
                margin: {
                    left: margin,
                    right: margin,
                    top: margin + 18,
                    bottom: margin + 5
                },
                tableWidth: pageWidth - (margin * 2),
                theme: 'grid',
                showHead: 'everyPage',
                horizontalPageBreak: false,
                didDrawPage: function (data) {
                    doc.setFontSize(6);
                    doc.setTextColor(150, 150, 150);
                    doc.text(
                        `Page ${data.pageNumber}`,
                        pageWidth - margin - 5,
                        pageHeight - margin
                    );
                },
                willDrawCell: function (data) {
                    if (data.cell.raw && data.cell.raw.toString().length > 20) {
                        data.cell.styles.fontSize = 4;
                    }

                    if ([1, 3, 26, 31].includes(data.column.index)) {
                        data.cell.styles.fontSize = 4;
                    }

                    if (data.cell.raw && data.cell.raw.toString().length > 50) {
                        data.cell.text = data.cell.raw.toString().substring(0, 47) + '...';
                    }
                }
            });

            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-');
            doc.save(`customers_${activeFilter?.type || 'all'}_${timestamp}.pdf`);

            toast.success(`PDF exported successfully`);

        } catch (error) {
            console.error("PDF export error:", error);
            toast.error("Failed to generate PDF");
        }
    };

    const renderReportsCell = (row) => {
        if (!row.reports) return <span className="text-muted">—</span>;

        try {
            const reports = typeof row.reports === "string" ? JSON.parse(row.reports) : row.reports;
            if (!Array.isArray(reports)) return <span className="text-muted">—</span>;

            const counts = { Paid: 0, Pending: 0, Unpaid: 0 };
            reports.forEach(r => counts[r.status]++);

            return (
                <div className="d-flex gap-1 align-items-center flex-wrap" style={{ minHeight: '20px' }}>
                    <span
                        className="badge rounded-pill px-2 py-1 fs-3xs d-inline-block"
                        style={{
                            backgroundColor: '#16a34a',
                            color: '#fff',
                            border: '1px solid #16a34a',
                            minWidth: '32px',
                            textAlign: 'center'
                        }}
                    >
                        {counts.Paid}Paid
                    </span>
                    <span
                        className="badge rounded-pill px-2 py-1 fs-3xs d-inline-block"
                        style={{
                            backgroundColor: '#d97706',
                            color: '#fff',
                            border: '1px solid #d97706',
                            minWidth: '32px',
                            textAlign: 'center'
                        }}
                    >
                        {counts.Pending}Pending
                    </span>
                    {counts.Unpaid > 0 && (
                        <span
                            className="badge rounded-pill px-2 py-1 fs-3xs d-inline-block"
                            style={{
                                backgroundColor: '#dc2626',
                                color: '#fff',
                                border: '1px solid #dc2626',
                                minWidth: '28px',
                                textAlign: 'center'
                            }}
                        >
                            {counts.Unpaid}Unpaid
                        </span>
                    )}
                    <small className="text-muted ms-1">({reports.length})</small>
                </div>
            );
        } catch {
            return <span className="text-muted">—</span>;
        }
    };

    const getColumns = () => {
        const baseColumns = [
            {
                name: "ID",
                selector: row => row.customer_id || "-",
                sortable: true,
                width: "70px"
            },
            {
                name: "Name",
                selector: row => row.full_name || "-",
                sortable: true,
                width: "150px"
            },
            {
                name: "Type",
                selector: row => row.type || "-",
                sortable: true,
                width: "110px"
            },
            {
                name: "Staff",
                selector: row => row.staff_name || "-",
                sortable: true,
                width: "120px"
            },
            {
                name: "Email",
                selector: row => row.email_address || "-",
                sortable: true,
                width: "220px"
            },
            {
                name: "Phone",
                selector: row => row.phone_number || "-",
                sortable: true,
                width: "130px"
            },
            {
                name: "DOB",
                selector: row => row.date_of_birth ? formatDate(row.date_of_birth) : "-",
                sortable: true,
                width: "110px"
            },
            {
                name: "ID Type",
                selector: row => row.id_type || "-",
                sortable: true,
                width: "90px"
            },
            {
                name: "ID Number",
                selector: row => row.id_number || "-",
                sortable: true,
                width: "110px"
            },
            {
                name: "Bill No",
                selector: row => row.bill_number || "-",
                sortable: true,
                width: "90px"
            },
            {
                name: "BILL.AMT",
                id: "bill_amount",
                selector: (row) => parseFloat(row.bill_amount) || 0,
                cell: (row) => (
                    <span className="font-semibold text-gray-800">
                        {formatCurrency(row.bill_amount)}
                    </span>
                ),
                width: "110px",
                sortable: true,
            },
            {
                name: "Down Pay",
                selector: row => row.down_pay ? row.down_pay : (row.bill_amount / row.emi_plan_duration).toFixed(2),
                sortable: true,
                width: "100px",
                style: {
                    color: 'green',
                    fontWeight: 'bold'
                }
            },
            {
                name: "Coll Fee",
                selector: row => {
                    if (row.coll_fee) return (+row.coll_fee).toFixed(2);

                    const monthly = (+row.bill_amount) / (+row.emi_plan_duration || 1);
                    const fee = fees.find(f => monthly >= +f.range_min && monthly <= +f.range_max);

                    return fee ? (+fee.collection_fee).toFixed(2) : 'inactive';
                },
                sortable: true,
                width: "100px",
                style: {
                    color: 'orange',
                    fontWeight: 'bold'
                }
            },
            {
                name: "Product",
                selector: row => {
                    const product = products.find(p => p.product_id === Number(row.product_name));
                    return product ? product.product_name : row.product_name || "-";
                },
                sortable: true,
                width: "200px"
            },
            {
                name: "EMI Plan",
                selector: row => row.emi_plan_duration || "-",
                sortable: true,
                width: "70px"
            },
            {
                name: "Payment Day",
                selector: row => row.payment_date ? formatPaymentDay(row.payment_date) : "-",
                sortable: true,
                width: "70px"
            },
            {
                name: "Bank Code",
                selector: row => row.applicantBankCode || "-",
                sortable: true,
                width: "90px"
            },
            {
                name: "DDA Ref",
                selector: row => row.boDDARefNo || "-",
                sortable: true,
                width: "130px"
            },
            {
                name: "BO Name",
                selector: row => row.boName || "-",
                sortable: true,
                width: "130px"
            },
            {
                name: "BO Tx Ref",
                selector: row => row.boTransactionRefNo || "-",
                sortable: true,
                width: "160px"
            },
            {
                name: "Client ID",
                selector: row => row.clientID || "-",
                sortable: true,
                width: "110px"
            },
            {
                name: "requestType",
                selector: row => row.requestType || "-",
                sortable: true,
                width: "90px"
            },
            {
                name: "Segment",
                selector: row => row.segment || "-",
                sortable: true,
                width: "90px"
            },
            {
                name: "Flag Status",
                cell: row => (
                    <Badge bg={row.cancelFlag === 1 ? "danger" : "success"} className="px-2 py-1">
                        {row.cancelFlag === 1 ? "Cancelled" : "Active"}
                    </Badge>
                ),
                sortable: true,
                width: "90px"
            },
            {
                name: "Reports",
                cell: row => renderReportsCell(row),
                width: "200px"
            }
        ];

        const conditionalColumns = {
            withGuarantor: [
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                },
                {
                    name: "Guarantor",
                    cell: row => row.guarantor_name ? (
                        <div className="d-flex align-items-center gap-2">
                            <FiUser size={14} className="text-success" />
                            <div>
                                <div className="fw-medium">{row.guarantor_name}</div>
                                {row.guarantor_id_number && (
                                    <small className="text-muted">ID: {row.guarantor_id_number}</small>
                                )}
                            </div>
                        </div>
                    ) : "-",
                    width: "180px"
                }
            ],
            guarantors: [
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                },
                {
                    name: "Guaranteeing",
                    cell: row => row.guaranteed_customer_name ? (
                        <div className="d-flex align-items-center gap-2">
                            <FiLink size={14} className="text-warning" />
                            <div>
                                <div className="fw-medium">{row.guaranteed_customer_name}</div>
                                {row.guaranteed_customer_id_number && (
                                    <small className="text-muted">ID: {row.guaranteed_customer_id_number}</small>
                                )}
                            </div>
                        </div>
                    ) : "-",
                    width: "180px"
                }
            ],
            missing: [
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                }
            ],
            failed: [
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                }
            ],
            incomplete: [
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                }
            ],
            active: [
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                },
                {
                    name: "Has Guarantor",
                    selector: row => row.has_guarantor_flag || "No",
                    sortable: true,
                    width: "130px"
                }
            ],
            default: [
                {
                    name: "Has Guarantor",
                    selector: row => row.has_guarantor_flag || "No",
                    sortable: true,
                    width: "130px"
                },
                {
                    name: "Purchase on",
                    selector: row => row.timestamp ? formatDateTime(row.timestamp) : "-",
                    sortable: true,
                    width: "200px"
                },
            ]
        };

        switch (activeFilter?.type) {
            case 'withGuarantor':
                return [...baseColumns, ...conditionalColumns.withGuarantor];
            case 'guarantors':
                return [...baseColumns, ...conditionalColumns.guarantors];
            case 'missing':
            case 'failed':
            case 'incomplete':
                return [...baseColumns, ...conditionalColumns[activeFilter.type]];
            case 'active':
                return [...baseColumns, ...conditionalColumns.active];
            default:
                return [...baseColumns, ...conditionalColumns.default];
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <Spinner animation="border" variant="secondary" />
                <span className="ms-2 text-muted">Loading customers data...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger d-flex align-items-center mt-4 shadow-sm border-0" role="alert" style={{ borderRadius: '12px' }}>
                <div className="flex-shrink-0 me-3">
                    <FiAlertTriangle size={24} className="text-danger" />
                </div>
                <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                        <h5 className="alert-heading mb-0 fw-bold me-2" style={{ fontSize: '1.1rem' }}>
                            Error loading customers
                        </h5>
                        <div className="px-2 py-1 bg-danger bg-opacity-10 text-danger rounded-pill small fw-medium">
                            Failed
                        </div>
                    </div>
                    <p className="mb-3 text-danger-emphasis small lh-sm">
                        {error}
                    </p>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-outline-danger btn-sm px-4 py-2 fw-semibold rounded-pill shadow-sm"
                            onClick={() => window.location.reload()}
                        >
                            <FiRefreshCw size={14} className="me-1" />
                            Retry
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm px-4 py-2 fw-semibold rounded-pill"
                            onClick={() => window.location.href = '/login'}
                        >
                            <FiLogIn size={14} className="me-1" />
                            Login Again
                        </button>
                    </div>
                </div>
            </div>

        );
    }

    const conditionalRowStyles = [
        {
            when: row => row.has_guarantor === 1,
            style: {
                backgroundColor: "#c5d5ffff",
                color: "#f8fafc",
            },
        },
        {
            when: row => row.has_guarantor !== 1,
            style: {
                backgroundColor: "#eff6ff",
                color: "#1e40af",
            },
        },
    ];

    return (
        <div className="container-fluid py-2">
            <ToastContainer position="top-right" autoClose={1000} />

            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 mb-2">
                                <p className="mb-0 text-muted fw-bold" style={{ fontSize: "0.8rem" }}>
                                    Live Data Overview
                                </p>

                                {activeFilter && (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="px-2 py-1 d-flex align-items-center gap-1">
                                            <span className="text-nowrap">
                                                Filter: {activeFilter.type === 'withGuarantor' ? 'Customers with Guarantor' :
                                                    activeFilter.type === 'guarantors' ? 'Guarantors' :
                                                        activeFilter.type === 'missing' ? 'Missing Status' :
                                                            activeFilter.type === 'failed' ? 'Failed (No Bank Token)' :
                                                                activeFilter.type === 'incomplete' ? 'All Incomplete' :
                                                                    activeFilter.type === 'justSignedUp' ? 'Just Signed Up' :
                                                                        activeFilter.type === 'active' ? 'Active Customers' : 'All Customers'}
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 text-white lh-1 fs-6"
                                                onClick={clearFilters}
                                            >
                                                <FiX size={12} />
                                            </Button>
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-1 mb-3">
                                {/* Total Customers */}
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-2 border border-primary border-1"
                                    style={{ backgroundColor: '#eff6ff', fontSize: '0.7rem' }}>
                                    <div className="rounded-circle" style={{ width: '4px', height: '4px', backgroundColor: '#1d4ed8' }} />
                                    <span className="fw-semibold" style={{ color: '#1e40af' }}>
                                        {stats.totalCustomers} Customers
                                    </span>
                                </div>

                                {/* With Guarantor */}
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-2 border border-success border-1"
                                    style={{ backgroundColor: '#dcfce7', fontSize: '0.7rem' }}>
                                    <div className="rounded-circle" style={{ width: '4px', height: '4px', backgroundColor: '#16a34a' }} />
                                    <span className="fw-semibold" style={{ color: '#166534' }}>
                                        {stats.customersWithGuarantor} w/ Guarantor
                                    </span>
                                </div>

                                {/* Guarantors */}
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-2 border border-warning border-1"
                                    style={{ backgroundColor: '#fef9c3', fontSize: '0.7rem' }}>
                                    <div className="rounded-circle" style={{ width: '4px', height: '4px', backgroundColor: '#d97706' }} />
                                    <span className="fw-semibold" style={{ color: '#854d0e' }}>
                                        {stats.guarantors} Guarantors
                                    </span>
                                </div>

                                {/* Incomplete */}
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-2 border border-danger border-1"
                                    style={{ backgroundColor: '#fee2e2', fontSize: '0.7rem' }}>
                                    <div className="rounded-circle" style={{ width: '4px', height: '4px', backgroundColor: '#dc2626' }} />
                                    <span className="fw-semibold" style={{ color: '#991b1b' }}>
                                        {stats.totalIncompleteCount} Incomplete
                                    </span>
                                </div>

                                {/* Just Signed Up */}
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-2 border border-purple border-1"
                                    style={{ backgroundColor: '#f3e8ff', fontSize: '0.7rem' }}>
                                    <div className="rounded-circle" style={{ width: '4px', height: '4px', backgroundColor: '#8b5cf6' }} />
                                    <span className="fw-semibold" style={{ color: '#7c3aed' }}>
                                        {stats.justSignedUpCount} New
                                    </span>
                                </div>

                                {/* Active */}
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-2 border border-success border-1"
                                    style={{ backgroundColor: '#d1fae5', fontSize: '0.7rem' }}>
                                    <div className="rounded-circle" style={{ width: '4px', height: '4px', backgroundColor: '#10b981' }} />
                                    <span className="fw-semibold" style={{ color: '#065f46' }}>
                                        {stats.activeCustomersCount} Active
                                    </span>
                                </div>
                            </div>

                        </div>

                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                            <Button
                                key={showAnswer ? randomMessage : "question"}
                                variant="link"
                                size="sm"
                                className={`d-flex align-items-center gap-1 p-0 text-decoration-none text-secondary 
        ${showAnswer ? "fade-text-answer" : "fade-text-question"}`}
                                style={{ fontSize: "0.75rem", fontWeight: "500" }}
                                onClick={handleAddGuarantorClick}
                                disabled={!displayData.length}
                            >
                                {!showAnswer ? (
                                    <>
                                        <span className="d-none d-sm-inline underline">
                                            Forgot to add guarantor
                                        </span>
                                        <span className="thinking-emoji ms-1">🤔?</span>
                                    </>
                                ) : (
                                    <span className="text-success ms-1">
                                        {randomMessage}
                                    </span>
                                )}
                            </Button>
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <FiDownload size={14} />
                                    <span className="d-none d-sm-inline">Export</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleExportCSV} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiFileText size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Export as CSV</div>
                                                <small className="text-muted">Comma-separated values file</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportExcel} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiFile size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Export as Excel</div>
                                                <small className="text-muted">Microsoft Excel format</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportPDF} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiDownload size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Export as PDF</div>
                                                <small className="text-muted">Portable Document Format</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={handleRefreshAll}
                                    disabled={loading}
                                >
                                    <FiRefreshCw size={14} className={loading ? "spinning" : ""} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </Button>

                                {activeFilter && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={clearFilters}
                                    >
                                        <span className="d-none d-sm-inline">Clear Filter</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-2">
                <Card.Body className="p-0">
                    <div
                        className="p-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => setCustomerStatsCollapsed(!customerStatsCollapsed)}
                        style={{
                            cursor: 'pointer',
                            backgroundColor: '#d4ffd3ff'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FiUsers size={14} className="text-primary me-2" />
                            <span className="fw-semibold" style={{ fontSize: 12 }}>
                                Customer Statistics{" "}
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    (Overview)
                                </span>
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <FiChevronDown
                                size={14}
                                className={`text-muted transition-transform ${customerStatsCollapsed ? '' : 'rotate-180'}`}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transform: customerStatsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                                }}
                            />
                        </div>
                    </div>

                    {!customerStatsCollapsed && (
                        <div className="pt-4">
                            <Row className="g-3">
                                <Col md={3}>
                                    <StatCard
                                        title="Total Customers"
                                        value={stats.totalCustomers}
                                        icon={<FiUsers />}
                                        color="#374151"
                                        filterType="all"
                                        isActive={!activeFilter || activeFilter.type === 'all'}
                                        onClick={() => handleStatCardClick('all')}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Active Customers"
                                        value={stats.activeCustomersCount}
                                        subtitle="Successfully created eGIRO"
                                        icon={<FiCheckCircle />}
                                        color="#10b981"
                                        filterType="active"
                                        isActive={activeFilter?.type === 'active'}
                                        onClick={() => handleStatCardClick('active')}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="With Guarantor"
                                        value={stats.customersWithGuarantor}
                                        subtitle="Customers who have guarantors"
                                        icon={<FiUserCheck />}
                                        color="#16a34a"
                                        filterType="withGuarantor"
                                        isActive={activeFilter?.type === 'withGuarantor'}
                                        onClick={() => handleStatCardClick('withGuarantor')}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Guarantors"
                                        value={stats.guarantors}
                                        subtitle="Acting as guarantor for others"
                                        icon={<FiUserX />}
                                        color="#d97706"
                                        filterType="guarantors"
                                        isActive={activeFilter?.type === 'guarantors'}
                                        onClick={() => handleStatCardClick('guarantors')}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <div
                        className="p-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => setEgiroStatsCollapsed(!egiroStatsCollapsed)}
                        style={{
                            cursor: 'pointer',
                            backgroundColor: '#fffbd2ff'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FiAlertTriangle size={14} className="text-primary me-2" />
                            <span className="fw-semibold" style={{ fontSize: 12 }}>
                                eGIRO Status{" "}
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    (Process Status)
                                </span>
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <FiChevronDown
                                size={14}
                                className={`text-muted transition-transform ${egiroStatsCollapsed ? '' : 'rotate-180'}`}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transform: egiroStatsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                                }}
                            />
                        </div>
                    </div>

                    {!egiroStatsCollapsed && (
                        <div className="pt-4">
                            <Row className="g-3">
                                <Col md={3}>
                                    <StatCard
                                        title="Incomplete (All)"
                                        value={stats.totalIncompleteCount}
                                        subtitle="Missing + Failed"
                                        icon={<FiAlertTriangle />}
                                        color="#dc2626"
                                        filterType="incomplete"
                                        isActive={activeFilter?.type === 'incomplete'}
                                        onClick={() => handleStatCardClick('incomplete')}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Just Signed Up"
                                        value={stats.justSignedUpCount}
                                        subtitle="eGIRO process not started"
                                        icon={<FiUser />}
                                        color="#8b5cf6"
                                        filterType="justSignedUp"
                                        isActive={activeFilter?.type === 'justSignedUp'}
                                        onClick={() => handleStatCardClick('justSignedUp')}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Missing Status"
                                        value={stats.missingCount}
                                        subtitle="Bank server down during signup"
                                        icon={<FiClock />}
                                        color="#f59e0b"
                                        filterType="missing"
                                        isActive={activeFilter?.type === 'missing'}
                                        onClick={() => handleStatCardClick('missing')}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Failed Creation"
                                        value={stats.failedCount}
                                        subtitle="No consent → eDDA failed"
                                        icon={<FiXCircle />}
                                        color="#ef4444"
                                        filterType="failed"
                                        isActive={activeFilter?.type === 'failed'}
                                        onClick={() => handleStatCardClick('failed')}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search customers by name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2">
                                    <Badge bg="primary" className="px-2 py-1">
                                        {displayData.length} Records
                                    </Badge>
                                    {activeFilter && (
                                        <Badge bg="info" className="px-2 py-1">
                                            {activeFilter.type === 'withGuarantor' ? 'Customers with Guarantor' :
                                                activeFilter.type === 'guarantors' ? 'Guarantors' :
                                                    activeFilter.type === 'missing' ? 'Missing Status' :
                                                        activeFilter.type === 'failed' ? 'Failed (No Bank Token)' :
                                                            activeFilter.type === 'incomplete' ? 'All Incomplete' :
                                                                activeFilter.type === 'justSignedUp' ? 'Just Signed Up' :
                                                                    activeFilter.type === 'active' ? 'Active Customers' : 'All Customers'}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        <DataTable
                            columns={getColumns()}
                            data={displayData}
                            conditionalRowStyles={conditionalRowStyles}
                            defaultSortFieldId="bill_amount"
                            defaultSortAsc={false}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[5, 10, 15, 20]}
                            highlightOnHover
                            responsive
                            striped
                            persistTableHead
                            fixedHeader
                            fixedHeaderScrollHeight="calc(100vh - 100px)"
                            customStyles={{
                                headCells: {
                                    style: {
                                        backgroundColor: "#f9fafb",
                                        fontWeight: "600",
                                        fontSize: "0.75rem",
                                        textTransform: "uppercase",
                                        color: "#374151",
                                        borderBottom: "1px solid #e5e7eb"
                                    },
                                },
                                cells: {
                                    style: {
                                        fontSize: "0.875rem",
                                        color: "#374151"
                                    }
                                }
                            }}
                            noDataComponent={
                                <div className="text-center py-5">
                                    <h6>No data available</h6>
                                    <p className="text-muted">
                                        {activeFilter
                                            ? `No customers found for this filter`
                                            : "No customers found"}
                                    </p>
                                </div>
                            }
                        />
                        <AddGuarantorModal
                            show={showGuarantorModal}
                            onHide={() => setShowGuarantorModal(false)}
                            customerId={selectedCustomer?.id}
                            customerName={selectedCustomer?.full_name}
                            refreshData={refreshAllData}
                        />
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default Customers;